import React from 'react'
import { Card, Select, Slider, Switch, Space, Typography, Divider, Row, Col, Button } from 'antd'
import { BulbOutlined, CloudOutlined, SunOutlined, SettingOutlined } from '@ant-design/icons'
import { LIGHTING_PRESETS } from '../Scene3D/lighting/EnhancedLightingSystem'
import { WEATHER_CONDITIONS, SEASONS } from '../Scene3D/environment/EnvironmentSystem'

const { Title, Text } = Typography
const { Option } = Select

interface EnhancedRenderingPanelProps {
  lightingPreset: string
  weatherCondition: string
  season: string
  timeOfDay: number
  enableAdvancedRendering: boolean
  onLightingPresetChange: (preset: string) => void
  onWeatherConditionChange: (weather: string) => void
  onSeasonChange: (season: string) => void
  onTimeOfDayChange: (time: number) => void
  onAdvancedRenderingToggle: (enabled: boolean) => void
  onClose: () => void
}

const EnhancedRenderingPanel: React.FC<EnhancedRenderingPanelProps> = ({
  lightingPreset,
  weatherCondition,
  season,
  timeOfDay,
  enableAdvancedRendering,
  onLightingPresetChange,
  onWeatherConditionChange,
  onSeasonChange,
  onTimeOfDayChange,
  onAdvancedRenderingToggle,
  onClose,
}) => {
  const formatTime = (hour: number) => {
    const h = Math.floor(hour)
    const m = Math.floor((hour - h) * 60)
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
  }

  const getTimeLabel = (hour: number) => {
    if (hour >= 6 && hour < 12) return '🌅 Morning'
    if (hour >= 12 && hour < 17) return '☀️ Afternoon'
    if (hour >= 17 && hour < 20) return '🌇 Evening'
    return '🌙 Night'
  }

  return (
    <Card
      title={
        <Space>
          <BulbOutlined />
          <span>Enhanced Rendering</span>
        </Space>
      }
      extra={
        <Button type="text" onClick={onClose}>
          ×
        </Button>
      }
      style={{
        position: 'fixed',
        top: '80px',
        right: '20px',
        width: '400px',
        maxHeight: '80vh',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Advanced Rendering Toggle */}
        <Row justify="space-between" align="middle">
          <Col>
            <Text strong>Advanced Rendering</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Enable realistic lighting and weather effects
            </Text>
          </Col>
          <Col>
            <Switch
              checked={enableAdvancedRendering}
              onChange={onAdvancedRenderingToggle}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </Col>
        </Row>

        {enableAdvancedRendering && (
          <>
            <Divider style={{ margin: '16px 0' }} />

            {/* Time of Day Control */}
            <div>
              <Space align="center" style={{ marginBottom: '8px' }}>
                <SunOutlined />
                <Text strong>Time of Day</Text>
                <Text type="secondary">
                  {formatTime(timeOfDay)} - {getTimeLabel(timeOfDay)}
                </Text>
              </Space>
              <Slider
                min={0}
                max={24}
                step={0.5}
                value={timeOfDay}
                onChange={onTimeOfDayChange}
                marks={{
                  0: '12 AM',
                  6: '6 AM',
                  12: '12 PM',
                  18: '6 PM',
                  24: '12 AM',
                }}
                tooltip={{
                  formatter: (value) => `${formatTime(value || 0)} - ${getTimeLabel(value || 0)}`,
                }}
              />
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Lighting Preset */}
            <div>
              <Space align="center" style={{ marginBottom: '8px' }}>
                <BulbOutlined />
                <Text strong>Lighting Preset</Text>
              </Space>
              <Select
                value={lightingPreset}
                onChange={onLightingPresetChange}
                style={{ width: '100%' }}
                placeholder="Select lighting preset"
              >
                {Object.entries(LIGHTING_PRESETS).map(([key, preset]) => (
                  <Option key={key} value={key}>
                    <Space>
                      <span>{preset.name}</span>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {preset.description}
                      </Text>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Weather Condition */}
            <div>
              <Space align="center" style={{ marginBottom: '8px' }}>
                <CloudOutlined />
                <Text strong>Weather Condition</Text>
              </Space>
              <Select
                value={weatherCondition}
                onChange={onWeatherConditionChange}
                style={{ width: '100%' }}
                placeholder="Select weather condition"
              >
                {Object.entries(WEATHER_CONDITIONS).map(([key, weather]) => (
                  <Option key={key} value={key}>
                    <Space>
                      <span>{weather.name}</span>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {weather.description}
                      </Text>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Season */}
            <div>
              <Space align="center" style={{ marginBottom: '8px' }}>
                <SettingOutlined />
                <Text strong>Season</Text>
              </Space>
              <Select
                value={season}
                onChange={onSeasonChange}
                style={{ width: '100%' }}
                placeholder="Select season"
              >
                {Object.entries(SEASONS).map(([key, seasonConfig]) => (
                  <Option key={key} value={key}>
                    <Space>
                      <span>{seasonConfig.name}</span>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {seasonConfig.dayLength}h daylight
                      </Text>
                    </Space>
                  </Option>
                ))}
              </Select>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Quick Presets */}
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block' }}>
                Quick Presets
              </Text>
              <Space wrap>
                <Button
                  size="small"
                  onClick={() => {
                    onLightingPresetChange('morning')
                    onTimeOfDayChange(8)
                    onWeatherConditionChange('clear')
                  }}
                >
                  🌅 Morning
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    onLightingPresetChange('midday')
                    onTimeOfDayChange(12)
                    onWeatherConditionChange('clear')
                  }}
                >
                  ☀️ Sunny Day
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    onLightingPresetChange('sunset')
                    onTimeOfDayChange(19)
                    onWeatherConditionChange('partly_cloudy')
                  }}
                >
                  🌇 Golden Hour
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    onLightingPresetChange('overcast')
                    onTimeOfDayChange(14)
                    onWeatherConditionChange('overcast')
                  }}
                >
                  ☁️ Overcast
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    onLightingPresetChange('night')
                    onTimeOfDayChange(22)
                    onWeatherConditionChange('clear')
                  }}
                >
                  🌙 Night
                </Button>
              </Space>
            </div>

            <Divider style={{ margin: '16px 0' }} />

            {/* Performance Info */}
            <div style={{ 
              background: '#f6f8fa', 
              padding: '12px', 
              borderRadius: '6px',
              border: '1px solid #e1e4e8'
            }}>
              <Text strong style={{ fontSize: '12px', color: '#586069' }}>
                💡 Performance Tips
              </Text>
              <br />
              <Text style={{ fontSize: '11px', color: '#586069' }}>
                • Advanced rendering may impact performance on older devices
                <br />
                • Disable for better mobile performance
                <br />
                • Weather effects are most visible in overcast/rain conditions
              </Text>
            </div>
          </>
        )}
      </Space>
    </Card>
  )
}

export default EnhancedRenderingPanel
